import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../assets/background.jpg"; // Placeholder for background image

const SendInquiry = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    course: "",
    name: "",
    email: "",
    countryCode: "+977",
    mobile: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const countryCodes = [
    { code: "+1", name: "United States" },
    { code: "+44", name: "United Kingdom" },
    { code: "+91", name: "India" },
    { code: "+977", name: "Nepal" },
    { code: "+61", name: "Australia" },
    { code: "+81", name: "Japan" },
    { code: "+86", name: "China" },
    { code: "+49", name: "Germany" },
    { code: "+33", name: "France" },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.course) newErrors.course = "Please select a course";
    if (!formData.name) newErrors.name = "Please enter your full name";
    if (!formData.email) newErrors.email = "Please enter your email";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Please enter a valid email";
    if (!formData.mobile) newErrors.mobile = "Please enter your mobile number";
    else if (!/^\d{7,15}$/.test(formData.mobile)) newErrors.mobile = "Mobile number must be 7-15 digits";
    if (!formData.message) newErrors.message = "Please enter your message";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    setShowConfirmation(true);
  };

  const confirmSubmission = async () => {
    setShowConfirmation(false);
    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "https://virtueistait.com";
      console.log(`Submitting inquiry to: ${apiUrl}/api/inquiry`);
      const res = await fetch(`${apiUrl}/api/inquiry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          mobile: `${formData.countryCode}${formData.mobile}`,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to submit inquiry.");
      }

      alert("🎉 Your inquiry has been submitted successfully! We'll get back to you soon.");
      navigate("/");
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      let errorMessage = error.message || "Something went wrong. Please try again later.";
      if (error.message.includes("Failed to fetch")) {
        errorMessage = "Unable to connect to the server. Please check your network or try again later.";
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const WhatsAppIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className="w-6 h-6"
      fill="#25D366"
    >
      <path
        d="M12 0C5.373 0 0 5.373 0 12c0 2.134.56 4.14 1.535 5.894L0 24l6.212-1.62A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22.001a10.001 10.001 0 01-5.258-1.494l-.366-.195-3.683.96 1.002-3.607-.24-.382A9.956 9.956 0 012 12c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10z"
        fill="#25D366"
      />
      <path
        d="M17.785 14.54c-.31-.155-1.83-.904-2.115-.99-.283-.087-.49-.017-.695.26-.206.276-.798.99-.965 1.194-.166.204-.332.22-.62.068-.287-.152-1.216-.473-2.316-1.508-.855-.804-1.432-1.8-1.6-2.087-.166-.287-.018-.44.125-.582.128-.128.287-.332.43-.498.144-.166.19-.287.287-.478.097-.19.048-.36-.024-.506-.072-.145-.65-1.566-.892-2.14-.235-.558-.473-.48-.65-.48-.166 0-.332 0-.506 0-.173 0-.453.065-.695.332-.24.267-.932.892-.932 2.175 0 1.283.955 2.52 1.086 2.695.132.173 1.877 2.872 4.558 4.028.634.276 1.13.44 1.518.563.637.204 1.216.173 1.674.105.51-.076 1.566-.637 1.788-1.252.222-.614.222-1.14.155-1.252-.067-.112-.24-.173-.55-.328z"
        fill="#FFFFFF"
      />
    </svg>
  );

  return (
    <div
      className="min-h-screen text-white"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#1e3a8a",
        background: `linear-gradient(135deg, rgba(30, 58, 138, 0.8), rgba(6, 182, 212, 0.8)), url(${backgroundImage})`,
      }}
    >
      {/* Header Section */}
      <div className="bg-black/30 backdrop-blur-sm text-white py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 animate-slow-fade-in">
            Let’s Get in Touch
            <span className="block text-yellow-500 mt-2 animate-slow-fade-in delay-1">
              Begin Your Learning Journey!
            </span>
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto animate-slow-fade-in delay-2">
            Curious about our courses? Drop us a message, and let’s start your journey together!
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-10 md:py-16 grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Contact Details Section */}
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl shadow-xl space-y-6">
          <h2 className="text-2xl font-bold text-white">Reach Out Directly</h2>
          <ContactDetail
            icon={<WhatsAppIcon />}
            title="WhatsApp"
            content="+977-9849489441"
            href="https://wa.me/+9779849489441"
          />
          <ContactDetail
            icon="📞"
            title="Contact"
            content="+977-9849489441,+977-9709023276"
            href="tel:+9779849489441"
          />
          <ContactDetail
            icon="☎️"
            title="Hotline"
            content="+977-9849489441"
            href="tel:+9779849489441"
          />
          <ContactDetail
            icon="✉️"
            title="Email"
            content="virtueistait@gmail.com"
            href="mailto:virtueistait@gmail.com"
          />
        </div>

        {/* Inquiry Form Section */}
        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-6">Course Inquiry</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <SelectInput
              label="Select a Course"
              name="course"
              value={formData.course}
              onChange={handleChange}
              options={[
                "Full Stack MERN", "React Native", "Python", "Data Science", "Django",
                "DevOps", "Flutter App Development", "UI/UX Design", "Frontend Web Development",
                "Full Stack Java", "Digital Marketing", "AI", "RHCSA", "RHCE", "Oracle",
                "AI & ML", "CCNA", "CCNP"
              ]}
              error={errors.course}
              icon="📚"
            />
            <TextInput
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              error={errors.name}
              icon="👤"
            />
            <TextInput
              label="Email Address"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              type="email"
              error={errors.email}
              icon="✉️"
            />
            <div className="flex space-x-4">
              <SelectInput
                label="Country Code"
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                options={countryCodes.map((c) => `${c.code} (${c.name})`)}
                error={errors.countryCode}
                icon="🌍"
                className="w-1/3"
              />
              <TextInput
                label="Mobile Number"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Enter your mobile number"
                type="tel"
                error={errors.mobile}
                icon="📱"
                className="w-2/3"
              />
            </div>
            <TextAreaInput
              label="Your Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Share your goals or questions..."
              error={errors.message}
              icon="💬"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-3 rounded-lg font-semibold transform transition-all duration-300 shadow-lg ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:from-blue-700 hover:to-blue-900 hover:scale-105"
              }`}
            >
              {isSubmitting ? "Submitting..." : "Send Inquiry Now"}
            </button>
          </form>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl shadow-2xl max-w-md w-full transform transition-all scale-100 animate-slow-fade-in">
            <h3 className="text-2xl font-bold text-white mb-4">Confirm Your Inquiry</h3>
            <div className="space-y-3 text-gray-200">
              <p><strong>Course:</strong> {formData.course}</p>
              <p><strong>Full Name:</strong> {formData.name}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Mobile Number:</strong> {formData.countryCode}{formData.mobile}</p>
              <p><strong>Message:</strong> {formData.message}</p>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
              >
                Edit
              </button>
              <button
                onClick={confirmSubmission}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline CSS for Animations */}
      <style>
        {`
          @keyframes slow-fade-in {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-slow-fade-in {
            animation: slow-fade-in 2s ease-out;
          }
          .delay-1 {
            animation-delay: 0.5s;
          }
          .delay-2 {
            animation-delay: 1s;
          }
          @media (max-width: 640px) {
            div[style*="background-attachment: fixed"] {
              background-attachment: scroll;
            }
          }
        `}
      </style>
    </div>
  );
};

const ContactDetail = ({ icon, title, content, href }) => (
  <a
    href={href}
    className="flex items-start space-x-4 hover:cursor-pointer"
  >
    <span className="mt-1">{icon}</span>
    <div>
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      <p className="text-white hover:text-blue-900">{content}</p>
    </div>
  </a>
);

const TextInput = ({ label, name, value, onChange, placeholder, type = "text", error, icon, className }) => (
  <div className={className}>
    <label className="block text-gray-700 font-medium mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-lg text-gray-500">
        {typeof icon === "string" ? icon : icon}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className={`w-full pl-10 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
          error ? "border-red-500" : "border-gray-300"
        } focus:outline-none`}
      />
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const TextAreaInput = ({ label, name, value, onChange, placeholder, error, icon }) => (
  <div>
    <label className="block text-gray-700 font-medium mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <span className="absolute left-3 top-4 text-lg text-gray-500">
        {icon}
      </span>
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className={`w-full pl-10 p-3 border rounded-lg h-32 focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
          error ? "border-red-500" : "border-gray-300"
        } focus:outline-none resize-none`}
      />
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const SelectInput = ({ label, name, value, onChange, options, error, icon, className }) => (
  <div className={className}>
    <label className="block text-gray-700 font-medium mb-2">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg md:top-1/2 md:-translate-y-1/2">{icon}</span>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required
        className={`w-full pl-10 p-3 border rounded-lg text-gray-600 focus:ring-2 focus:ring-blue-500 transition-all duration-200 appearance-none ${
          error ? "border-red-500" : "border-gray-300"
        } focus:outline-none`}
      >
        <option value="" disabled>
          {name === "countryCode" ? "Select Country..." : "Choose a Course..."}
        </option>
        {options.map((opt) => (
          <option key={opt} value={name === "countryCode" ? opt.split(" ")[0] : opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

export default SendInquiry;
